/** EVERY network query the portal makes, defined once — no tab or card
 *  declares one inline.
 *
 *  These were previously declared inline at three call sites (dashboard, the
 *  network page, the wrong-network banner) under the same two query keys but
 *  with TWO DIFFERENT shapes: the network page unwrapped `r.data`, the other
 *  two cached the whole axios response. TanStack Query keys the cache on the
 *  key alone, so whichever mounted first decided the shape for everybody —
 *  arriving at /network from the dashboard made `wireless?.ssid` undefined
 *  (the value was at `wireless.data.ssid`) and the page reported "No WiFi
 *  networks configured", while a hard reload of the same URL worked.
 *
 *  One key, one fetcher, one shape — always the unwrapped payload. For the
 *  filtered devices list the same rule extends to parameters: the key and the
 *  fetcher are built from the SAME params object inside one hook, so a filter
 *  can never be in the request but out of the key (which would serve the
 *  previous filter's rows from cache and look like a permissions bug). */
import { keepPreviousData, useQuery } from "@tanstack/react-query";

import { tenantApi } from "@/lib/api/client";
import type { PortalCapacityResponse } from "@nodus/capacity";
import type { NetworkDevice, NetworkInfo, NetworkPlan, PaginatedResponse, SpeedtestHistoryItem, WirelessInfo } from "@/lib/api/client";

/** `retry: false` keeps a 404 ("not assigned yet") fast — both pages branch on
 *  it to show a friendly empty state rather than a failure. */
/** Exported so invalidation can never drift from the fetcher's key. */
export const WIRELESS_QUERY_KEY = ["wireless"] as const;
export const NETWORK_INFO_QUERY_KEY = ["network-info"] as const;
export const NETWORK_DEVICES_QUERY_KEY = ["network-devices"] as const;
export const SPEEDTEST_HISTORY_QUERY_KEY = ["speedtest-history"] as const;
export const NETWORK_CAPACITY_QUERY_KEY = ["network-capacity"] as const;
export const NETWORK_PLAN_QUERY_KEY = ["network-plan"] as const;

export function useWirelessInfo(options?: { enabled?: boolean }) {
  return useQuery<WirelessInfo>({
    queryKey: WIRELESS_QUERY_KEY,
    queryFn: () => tenantApi.getWireless().then((r) => r.data),
    retry: false,
    ...options,
  });
}

export function useNetworkInfo(options?: { enabled?: boolean }) {
  return useQuery<NetworkInfo>({
    queryKey: NETWORK_INFO_QUERY_KEY,
    queryFn: () => tenantApi.getNetwork().then((r) => r.data),
    retry: false,
    staleTime: 5 * 60_000,
    ...options,
  });
}

export function useSpeedtestHistory() {
  return useQuery<SpeedtestHistoryItem[]>({
    queryKey: SPEEDTEST_HISTORY_QUERY_KEY,
    queryFn: () => tenantApi.listDiagnosticSessions().then((r) => r.data),
    staleTime: 30_000,
  });
}

export function useNetworkCapacity(options?: { enabled?: boolean }) {
  return useQuery<PortalCapacityResponse>({
    queryKey: NETWORK_CAPACITY_QUERY_KEY,
    queryFn: () => tenantApi.getNetworkCapacity().then((r) => r.data),
    retry: false,
    staleTime: 60_000,
    ...options,
  });
}

/** The plan ladder (#313) — every priced tier for the member's network with
 *  the member's own marked. Prices go to every member; `can_change` gates the
 *  switch buttons only. */
export function useNetworkPlan(options?: { enabled?: boolean }) {
  return useQuery<NetworkPlan>({
    queryKey: NETWORK_PLAN_QUERY_KEY,
    queryFn: () => tenantApi.getNetworkPlan().then((r) => r.data),
    retry: false,
    staleTime: 60_000,
    ...options,
  });
}

/** Filters for the devices table — mirrors the query string of
 *  /network/devices exactly; the URL is the source of truth. */
export interface DeviceListFilters {
  scope: "mine" | "everyone";
  /** Active `show` toggles: online, offline, static, blocked, history. */
  show: string[];
  q: string;
  page: number;
}

/** The one devices query (#310). The queryKey embeds the whole params object
 *  (TanStack hashes it with stable key order), so EVERY filter is in the key
 *  by construction — this page has already shipped the shared-key bug once.
 *  `show` is sorted so chip toggle order can't split the cache. */
export function useNetworkDevices(filters: DeviceListFilters, options?: { enabled?: boolean }) {
  const params = {
    scope: filters.scope,
    show: [...filters.show].sort().join(","),
    q: filters.q,
    page: filters.page,
  };
  return useQuery<PaginatedResponse<NetworkDevice>>({
    queryKey: [...NETWORK_DEVICES_QUERY_KEY, params],
    queryFn: () =>
      tenantApi
        .listNetworkDevices({
          scope: params.scope,
          show: params.show || undefined,
          q: params.q || undefined,
          page: params.page,
        })
        .then((r) => r.data),
    // A 404 means the endpoint hasn't shipped yet — the tab degrades to an
    // empty state rather than hammering retries; a 403 means a member asked
    // for scope=everyone, which the server (correctly) refuses.
    retry: false,
    placeholderData: keepPreviousData,
    refetchInterval: 60_000,
    ...options,
  });
}
